import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '../api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const res = await authApi.me();
      if (res.data.success) setUser(res.data.data);
      else setUser(null);
    } catch (err) {
      // 403 with COMPANY_SUSPENDED means the session was just killed server-side
      if (err.response?.status === 403) {
        const msg = err.response?.data?.message || '';
        if (msg.startsWith('COMPANY_SUSPENDED') || msg.startsWith('COMPANY_CHURNED')) {
          setUser({ __suspended: true, message: msg.split(':')[1] });
          return;
        }
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
    const handler = () => { setUser(null); setLoading(false); };
    window.addEventListener('tracern:unauthorized', handler);
    return () => window.removeEventListener('tracern:unauthorized', handler);
  }, [fetchMe]);

  const login = async (employeeId, password) => {
    const res = await authApi.login(employeeId, password);
    if (res.data.success) {
      const msg = res.data.message || '';
      if (msg.startsWith('MFA_REQUIRED:')) {
        // Don't set user — return special object so Login.js can redirect to MFA verify
        return { mfaRequired: true, tempToken: msg.split(':')[1] };
      }
      if (msg === 'MFA_SETUP_REQUIRED') {
        // Company policy requires MFA — user is logged in but must set up MFA now
        // Set user so they can call /api/auth/mfa/setup, but signal setup is required
        setUser(res.data.data);
        return { ...res.data.data, mfaSetupRequired: true };
      }
      setUser(res.data.data);
      return res.data.data;
    }
    throw new Error(res.data.message);
  };

  const logout = async () => {
    await authApi.logout();
    setUser(null);
  };

  const refreshUser = fetchMe;

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
